import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';

interface CreateVendorFormBlockProps {
  onAction: (message: string) => void;
}

const CreateVendorFormBlock: React.FC<CreateVendorFormBlockProps> = ({ onAction }) => {
  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [contact, setContact] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { t } = useTranslation('purchaseflow');

  const handleSubmit = () => {
    if (!name.trim()) return;
    setSubmitted(true);
    // Send as structured message the agent can parse
    const parts = [`Create vendor: ${name.trim()}`];
    if (taxId.trim()) parts.push(`Tax ID: ${taxId.trim()}`);
    if (contact.trim()) parts.push(`Contact: ${contact.trim()}`);
    if (phone.trim()) parts.push(`Phone: ${phone.trim()}`);
    if (email.trim()) parts.push(`Email: ${email.trim()}`);
    onAction(parts.join(', '));
  };

  if (submitted) {
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
        <Building2 className="w-4 h-4" />
        {t('vendorForm.creating')} <strong className="ml-1">{name}</strong>...
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-xs">
      <div className="flex items-center gap-2 mb-3 text-gray-700 dark:text-gray-300 font-semibold">
        <Building2 className="w-4 h-4 text-blue-500" />
        {t('vendorForm.title')}
      </div>

      <div className="space-y-2">
        <div>
          <label className="block text-gray-500 dark:text-gray-400 mb-0.5">{t('vendorForm.companyName')} *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Amphenol Taiwan"
            className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-gray-500 dark:text-gray-400 mb-0.5">{t('vendorForm.taxId')}</label>
            <input
              type="text"
              value={taxId}
              onChange={e => setTaxId(e.target.value)}
              placeholder="12345678"
              maxLength={8}
              className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-gray-500 dark:text-gray-400 mb-0.5">{t('vendorForm.contactPerson')}</label>
            <input
              type="text"
              value={contact}
              onChange={e => setContact(e.target.value)}
              placeholder="Name"
              className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-gray-500 dark:text-gray-400 mb-0.5">{t('vendorForm.phone')}</label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="02-xxxx-xxxx"
              className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-gray-500 dark:text-gray-400 mb-0.5">{t('vendorForm.email')}</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="name@company.com"
              className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-3">
        <button
          onClick={() => onAction('Show me the vendor list')}
          className="px-3 py-1.5 rounded-md text-xs text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
        >
          {t('cancel')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={!name.trim()}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {t('vendorForm.createVendor')}
        </button>
      </div>
    </div>
  );
};

export default CreateVendorFormBlock;
